import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2, DollarSign, ShoppingCart, Users, Heart, Download, FileSpreadsheet, CalendarDays,
  ChevronLeft, ChevronRight, Loader2, AlertCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AdminLayout from '../components/AdminLayout';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { apiFetch } from '../lib/api';

interface EstateOption {
  id: number;
  name: string;
}

interface Kpis {
  monthlyRevenue: number;
  monthlyCommission: number;
  baseCommission: number;
  charityReserve: number;
  monthlyOrders: number;
  activeCustomers: number;
  revenueChangePercent: number;
  ordersChangePercent: number;
  customersChangePercent: number;
}

interface DailyTrend {
  date: string;
  orders: number;
  revenue: number;
}

interface SummaryData {
  estate: { estateId: number; estateName: string };
  month: string;
  lastDay: string;
  kpis: Kpis;
  dailyTrends: DailyTrend[];
}

interface EstateOrder {
  id: number;
  orderNum: string;
  createdAt: number;
  name: string;
  phoneMasked: string;
  totalPrice: number;
  estateCommission: number;
  charityReserve: number;
  paymentConfirmed: boolean;
  orderCompleted: boolean;
  deliveryDate: string;
  deliveryTime: string;
}

interface OrdersData {
  orders: EstateOrder[];
  total: number;
  page: number;
  limit: number;
}

const CHART_MAX_HEIGHT = 200;

function formatMonthInput(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatCurrency(n: number): string {
  return `HK$${n.toLocaleString('zh-HK')}`;
}

function formatDateShort(ts: number, locale = 'zh-HK'): string {
  return new Date(ts * 1000).toLocaleDateString(locale, { timeZone: 'Asia/Hong_Kong' });
}

const EstateManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // 日期格式跟隨介面語言（zh-HK / en-GB）
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en-GB' : 'zh-HK';
  const token = localStorage.getItem('admin_token');

  const [estates, setEstates] = useState<EstateOption[]>([]);
  const [selectedEstateId, setSelectedEstateId] = useState<number | null>(null);
  const [month, setMonth] = useState<string>(formatMonthInput(new Date()));
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [ordersData, setOrdersData] = useState<OrdersData | null>(null);
  const [page, setPage] = useState(1);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  }, [navigate]);

  const loadEstates = useCallback(async () => {
    try {
      const res = await apiFetch('/api/public/admin/estate-dashboard/estates');
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('partner:estate.loadEstatesFailed'));
      const list = (json.estates || []) as EstateOption[];
      setEstates(list);
      if (list.length > 0 && !selectedEstateId) {
        setSelectedEstateId(list[0].id);
      }
    } catch (e: any) {
      setError(e.message || t('partner:estate.loadEstatesFailed'));
    }
  }, [handleUnauthorized, selectedEstateId]);

  const loadSummary = useCallback(async () => {
    if (!selectedEstateId) {
      setLoadingSummary(false);
      return;
    }
    setLoadingSummary(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/estate-dashboard/summary?estate_id=${selectedEstateId}&month=${month}`);
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('partner:estate.loadSummaryFailed'));
      setSummary(json as SummaryData);
    } catch (e: any) {
      setError(e.message || t('partner:estate.loadSummaryFailed'));
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  }, [selectedEstateId, month, handleUnauthorized]);

  const loadOrders = useCallback(async () => {
    if (!selectedEstateId) {
      setLoadingOrders(false);
      return;
    }
    setLoadingOrders(true);
    try {
      const res = await apiFetch(`/api/public/admin/estate-dashboard/orders?estate_id=${selectedEstateId}&month=${month}&page=${page}&limit=20`);
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('partner:estate.loadOrdersFailed'));
      setOrdersData(json as OrdersData);
    } catch (e: any) {
      setError(e.message || t('partner:estate.loadOrdersFailed'));
      setOrdersData(null);
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedEstateId, month, page, handleUnauthorized]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadEstates();
  }, [token, navigate, loadEstates]);

  useEffect(() => {
    if (selectedEstateId) {
      loadSummary();
      loadOrders();
    }
  }, [selectedEstateId, month, page, loadSummary, loadOrders]);

  interface ReportData {
    estate: { estateId: number; estateName: string };
    month: string;
    lastDay: string;
    summary: {
      orderCount: number;
      revenue: number;
      baseCommission: number;
      charityReserve: number;
      totalCommission: number;
      note: string;
    };
    orders: Array<{
      orderNum: string;
      createdAt: number;
      deliveryDate: string;
      name: string;
      phoneMasked: string;
      totalPrice: number;
      estateCommission: number;
      charityReserve: number;
      paymentConfirmed: boolean;
      orderCompleted: boolean;
    }>;
  }

  const handleDownloadReport = async () => {
    if (!selectedEstateId) return;
    setDownloading(true);
    try {
      const res = await apiFetch(`/api/public/admin/estate-dashboard/report?estate_id=${selectedEstateId}&month=${month}`);
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('partner:estate.downloadFailed'));

      const data = json as ReportData;
      const wb = XLSX.utils.book_new();

      const summaryRows = [
        [t('partner:estate.xls.estateName'), data.estate.estateName],
        [t('partner:estate.xls.month'), data.month],
        [t('partner:estate.xls.settleDate'), data.lastDay],
        [t('partner:estate.xls.orderCount'), data.summary.orderCount],
        [t('partner:estate.xls.revenue'), data.summary.revenue],
        [t('partner:estate.xls.baseCommission'), data.summary.baseCommission],
        [t('partner:estate.xls.charityReserve'), data.summary.charityReserve],
        [t('partner:estate.xls.totalCommission'), data.summary.totalCommission],
        ['', ''],
        [t('partner:estate.xls.note'), data.summary.note],
      ];
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows);
      XLSX.utils.book_append_sheet(wb, summaryWs, t('partner:estate.xls.summarySheet'));

      const orderRows: (string | number)[][] = [
        [t('partner:estate.colOrderNum'), t('partner:estate.colOrderDate'), t('partner:estate.colDeliveryDate'), t('partner:estate.xls.residentName'), t('partner:estate.xls.phone'), t('partner:estate.xls.amountXls'), t('partner:estate.xls.commissionXls'), t('partner:estate.xls.charityXls'), t('partner:estate.xls.paymentStatus'), t('partner:estate.xls.orderStatus')],
      ];
      for (const o of data.orders) {
        orderRows.push([
          o.orderNum,
          o.createdAt ? new Date(o.createdAt * 1000).toLocaleDateString(dateLocale) : '',
          o.deliveryDate,
          o.name,
          o.phoneMasked,
          o.totalPrice,
          o.estateCommission,
          o.charityReserve,
          o.paymentConfirmed ? t('partner:estate.paid') : t('partner:estate.unpaid'),
          o.orderCompleted ? t('partner:estate.completed') : t('partner:estate.processing'),
        ]);
      }
      const ordersWs = XLSX.utils.aoa_to_sheet(orderRows);
      XLSX.utils.book_append_sheet(wb, ordersWs, t('partner:estate.xls.ordersSheet'));

      const filename = `estate_report_${data.estate.estateName.replace(/\s+/g, '_')}_${data.month}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (e: any) {
      setError(e.message || t('partner:estate.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadOrdersCSV = () => {
    if (!ordersData?.orders.length) return;
    const headers = [t('partner:estate.colOrderNum'), t('partner:estate.colOrderDate'), t('partner:estate.colDeliveryDate'), t('partner:estate.xls.residentName'), t('partner:estate.xls.phone'), t('partner:estate.xls.amountCsv'), t('partner:estate.xls.commissionCsv'), t('partner:estate.xls.charityCsv'), t('partner:estate.xls.paymentStatus'), t('partner:estate.xls.orderStatus')];
    const rows = ordersData.orders.map((o) => [
      o.orderNum,
      o.createdAt ? formatDateShort(o.createdAt, dateLocale) : '',
      o.deliveryDate || '',
      o.name,
      o.phoneMasked,
      o.totalPrice,
      o.estateCommission,
      o.charityReserve,
      o.paymentConfirmed ? t('partner:estate.paid') : t('partner:estate.unpaid'),
      o.orderCompleted ? t('partner:estate.completed') : t('partner:estate.processing'),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `estate_orders_${summary?.estate.estateName.replace(/\s+/g, '_') || 'estate'}_${month}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const changeMonth = (offset: number) => {
    const [y, m] = month.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1 + offset, 1));
    setMonth(formatMonthInput(next));
    setPage(1);
  };

  const daily = summary?.dailyTrends ?? [];
  const maxRevenue = daily.reduce((max, d) => Math.max(max, d.revenue), 0);
  const maxOrders = daily.reduce((max, d) => Math.max(max, d.orders), 0);

  const totalPages = ordersData ? Math.ceil(ordersData.total / ordersData.limit) : 0;

  // label 用 key 存（render 時才 t()），避免 useMemo 在切換語言後顯示舊語言
  const kpiCards = useMemo(() => {
    const kpis = summary?.kpis;
    return [
      {
        labelKey: 'kpiRevenue',
        value: formatCurrency(kpis?.monthlyRevenue ?? 0),
        change: kpis?.revenueChangePercent ?? 0,
        icon: DollarSign,
        color: 'text-green-600',
        bg: 'bg-green-100 dark:bg-green-900/30',
      },
      {
        labelKey: 'kpiCommission',
        value: formatCurrency(kpis?.baseCommission ?? 0),
        change: kpis?.revenueChangePercent ?? 0,
        icon: Building2,
        color: 'text-orange-600',
        bg: 'bg-orange-100 dark:bg-orange-900/30',
      },
      {
        labelKey: 'kpiCharity',
        value: formatCurrency(kpis?.charityReserve ?? 0),
        change: kpis?.revenueChangePercent ?? 0,
        icon: Heart,
        color: 'text-pink-600',
        bg: 'bg-pink-100 dark:bg-pink-900/30',
      },
      {
        labelKey: 'kpiOrders',
        value: String(kpis?.monthlyOrders ?? 0),
        change: kpis?.ordersChangePercent ?? 0,
        icon: ShoppingCart,
        color: 'text-blue-600',
        bg: 'bg-blue-100 dark:bg-blue-900/30',
      },
      {
        labelKey: 'kpiCustomers',
        value: String(kpis?.activeCustomers ?? 0),
        change: kpis?.customersChangePercent ?? 0,
        icon: Users,
        color: 'text-purple-600',
        bg: 'bg-purple-100 dark:bg-purple-900/30',
      },
    ];
  }, [summary]);

  return (
    <AdminLayout currentPage="estate-dashboard" onLogout={handleUnauthorized}>
      <div className="p-4 md:p-8">
        {/* 頁首 */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-orange-600" />
            {t('partner:estate.title')}
          </h1>
          <LanguageSwitcher size="sm" />
        </div>

        {/* 篩選列 */}
        <div className="mb-6 flex flex-wrap items-center gap-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">{t('partner:estate.estateLabel')}</label>
            <select
              value={selectedEstateId ?? ''}
              onChange={(e) => {
                setSelectedEstateId(Number(e.target.value));
                setPage(1);
              }}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
            >
              {estates.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => changeMonth(-1)} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
              <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-gray-500" />
              <input
                type="month"
                value={month}
                onChange={(e) => { setMonth(e.target.value); setPage(1); }}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
            </div>
            <button onClick={() => changeMonth(1)} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <div className="flex-1" />

          <button
            onClick={handleDownloadReport}
            disabled={downloading || !selectedEstateId}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            {t('partner:estate.downloadReport')}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {estates.length === 0 && !loadingSummary && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 flex items-center gap-2 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{t('partner:estate.noEstates')}</span>
          </div>
        )}

        {loadingSummary ? (
          <div className="flex items-center justify-center py-20 text-gray-600 dark:text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            {t('partner:estate.loading')}
          </div>
        ) : summary ? (
          <>
            {/* KPI 卡片 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              {kpiCards.map((kpi) => (
                <div key={kpi.labelKey} className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${kpi.bg}`}>
                      <kpi.icon className={`w-6 h-6 ${kpi.color}`} />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{t(`partner:estate.${kpi.labelKey}`)}</div>
                      <div className="text-xl font-bold text-gray-800 dark:text-white">{kpi.value}</div>
                      {kpi.change !== undefined && (
                        <div className={`text-xs font-medium ${kpi.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {kpi.change >= 0 ? '↑' : '↓'} {Math.abs(kpi.change)}% {t('partner:estate.vsLastMonth')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 佣金明細提示 */}
            <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-sm text-orange-800 dark:text-orange-200">
              <strong>{t('partner:estate.noteCommissionLabel')}</strong>{t('partner:estate.noteCommissionTail')}<strong>{t('partner:estate.noteCharityLabel')}</strong>{t('partner:estate.noteCharityTail', { date: summary.lastDay })}
            </div>

            {/* 趨勢圖表 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* 每日營業額 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">{t('partner:estate.dailyRevenue')}</h2>
                {daily.length > 0 ? (
                  <div className="overflow-x-auto">
                    <div className="flex items-end gap-1 min-w-max" style={{ height: `${CHART_MAX_HEIGHT + 28}px` }}>
                      {daily.map((d) => {
                        const barHeight = d.revenue > 0 && maxRevenue > 0
                          ? Math.max((d.revenue / maxRevenue) * CHART_MAX_HEIGHT, 2)
                          : 2;
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center justify-end min-w-[20px] sm:min-w-[24px] relative">
                            {d.revenue > 0 && (
                              <div className="mb-1 text-[8px] sm:text-[9px] text-gray-700 dark:text-gray-300 whitespace-nowrap leading-none">
                                ${Math.round(d.revenue).toLocaleString('zh-HK')}
                              </div>
                            )}
                            <div
                              className={`w-full rounded-t ${d.revenue > 0 ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                              style={{ height: `${barHeight}px` }}
                              title={`${d.date}: ${formatCurrency(d.revenue)}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-1 min-w-max mt-2">
                      {daily.map((d, i) => (
                        <div key={d.date} className="flex-1 min-w-[20px] sm:min-w-[24px] text-center text-xs text-gray-600 dark:text-gray-400">
                          {i % 5 === 0 ? d.date.slice(5) : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="py-10 text-center text-gray-600 dark:text-gray-400">{t('partner:estate.noData')}</div>
                )}
              </div>

              {/* 每日訂單數 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">{t('partner:estate.dailyOrders')}</h2>
                {daily.length > 0 ? (
                  <div className="overflow-x-auto">
                    <div className="flex items-end gap-1 min-w-max" style={{ height: `${CHART_MAX_HEIGHT + 28}px` }}>
                      {daily.map((d) => {
                        const barHeight = d.orders > 0 && maxOrders > 0
                          ? Math.max((d.orders / maxOrders) * CHART_MAX_HEIGHT, 2)
                          : 2;
                        return (
                          <div key={`orders-${d.date}`} className="flex-1 flex flex-col items-center justify-end min-w-[20px] sm:min-w-[24px] relative">
                            {d.orders > 0 && (
                              <div className="mb-1 text-[8px] sm:text-[9px] text-gray-700 dark:text-gray-300 whitespace-nowrap leading-none">
                                {d.orders}
                              </div>
                            )}
                            <div
                              className={`w-full rounded-t ${d.orders > 0 ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                              style={{ height: `${barHeight}px` }}
                              title={t('partner:estate.ordersTooltip', { date: d.date, count: d.orders })}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-1 min-w-max mt-2">
                      {daily.map((d, i) => (
                        <div key={`label-${d.date}`} className="flex-1 min-w-[20px] sm:min-w-[24px] text-center text-xs text-gray-600 dark:text-gray-400">
                          {i % 5 === 0 ? d.date.slice(5) : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="py-10 text-center text-gray-600 dark:text-gray-400">{t('partner:estate.noData')}</div>
                )}
              </div>
            </div>

            {/* 訂單明細表 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{t('partner:estate.ordersTitle')}</h2>
                <button
                  onClick={handleDownloadOrdersCSV}
                  disabled={!ordersData?.orders.length}
                  className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  {t('partner:estate.downloadCsv')}
                </button>
              </div>

              {loadingOrders ? (
                <div className="flex items-center justify-center py-16 text-gray-600 dark:text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t('partner:estate.loadingOrders')}
                </div>
              ) : ordersData && ordersData.orders.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colOrderNum')}</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colOrderDate')}</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colDeliveryDate')}</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colResident')}</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colAmount')}</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colCommission')}</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colCharity')}</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colPayment')}</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-800 dark:text-white">{t('partner:estate.colStatus')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {ordersData.orders.map((o) => (
                          <tr key={o.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 text-sm text-gray-800 dark:text-white font-mono">{o.orderNum}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDateShort(o.createdAt, dateLocale)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{o.deliveryDate || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">
                              <div>{o.name}</div>
                              <div className="text-xs text-gray-500">{o.phoneMasked}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-800 dark:text-white">{formatCurrency(o.totalPrice)}</td>
                            <td className="px-4 py-3 text-sm text-right text-orange-600 font-medium">{formatCurrency(o.estateCommission)}</td>
                            <td className="px-4 py-3 text-sm text-right text-pink-600 font-medium">{formatCurrency(o.charityReserve)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${o.paymentConfirmed ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                                {o.paymentConfirmed ? t('partner:estate.paid') : t('partner:estate.unpaid')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${o.orderCompleted ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
                                {o.orderCompleted ? t('partner:estate.completed') : t('partner:estate.processing')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* 分頁 */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {t('partner:estate.pageInfo', { total: ordersData.total, page, totalPages })}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page <= 1}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          {t('partner:estate.prevPage')}
                        </button>
                        <button
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          {t('partner:estate.nextPage')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="px-6 py-16 text-center text-gray-600 dark:text-gray-400">{t('partner:estate.noOrders')}</div>
              )}
            </div>
          </>
        ) : (
          <div className="py-20 text-center text-gray-600 dark:text-gray-400">{t('partner:estate.loadFailedGeneric')}</div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EstateManagerDashboard;
